// src/pages/Dashboard/DashboardHome.js

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import "../../styles/DashboardHome.css";
import "../../styles/Tables.css";

import Skeleton from "../../components/Skeleton";
import PieChartScopes from "../../components/charts/PieChartScopes";
import BarChartScopes from "../../components/charts/BarChartScopes";
import OverallEmissionsLineChart from "../../components/charts/OverallEmissionsLineChart";
import ScopeTrendsLineChart from "../../components/charts/ScopeTrendsLineChart";

import {
  fetchClientDataApi,
  fetchUploadHistoryApi,
  deleteEntryApi,
} from "../../lib/api";
import { downloadPdfReport } from "../../lib/pdfReport";

import TimeframeSelector from "../../components/TimeframeSelector";
import { filterByTimeframe } from "../../lib/timeframe";

function safeNumber(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}

function formatPct(pct) {
  if (!Number.isFinite(pct)) return "-";
  return `${Math.abs(pct).toFixed(1)}%`;
}

export default function DashboardHome() {
  const navigate = useNavigate();

  const [rawData, setRawData] = useState(null);
  const [rawHistory, setRawHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const [timeframe, setTimeframe] = useState("all");
  const [scopeTrendMode, setScopeTrendMode] = useState("cumulative"); // "cumulative" | "daily"

  // Table filter
  const [scopeFilter, setScopeFilter] = useState("all"); // "all" | "Scope 1" | "Scope 2" | "Scope 3"

  // Disable only the row being deleted
  const [deletingId, setDeletingId] = useState(null);

  // Centralised reload so delete can refresh everything cleanly
  const reloadDashboard = useCallback(async () => {
    const [dataRes, historyRes] = await Promise.all([fetchClientDataApi(), fetchUploadHistoryApi()]);
    setRawData(dataRes);
    setRawHistory(historyRes || []);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await reloadDashboard();
      } catch (err) {
        toast.error(err?.message || "Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    })();
  }, [reloadDashboard]);

  const client = useMemo(() => rawData?.client || "", [rawData]);

  // Base summary from backend (all-time)
  const items = useMemo(() => rawData?.data?.parsed_items || [], [rawData]);
  const sustainabilitySettings = useMemo(
    () => rawData?.data?.sustainability_settings || {},
    [rawData]
  );

  // Base summary from backend (all-time), with frontend fallback for legacy
  // or stale summary payloads.
  const baseSummary = useMemo(() => {
    const s = rawData?.data?.emissions_summary;
    const backendSummary = {
      scope_1_kgco2e: safeNumber(s?.scope_1_kgco2e),
      scope_2_kgco2e: safeNumber(s?.scope_2_kgco2e),
      scope_3_kgco2e: safeNumber(s?.scope_3_kgco2e),
      total_kgco2e: safeNumber(s?.total_kgco2e),
    };

    const backendHasScopeData =
      backendSummary.scope_1_kgco2e > 0 ||
      backendSummary.scope_2_kgco2e > 0 ||
      backendSummary.scope_3_kgco2e > 0;

    if (backendHasScopeData || backendSummary.total_kgco2e > 0) {
      return backendSummary;
    }

    const itemSummary = (items || []).reduce(
      (acc, item) => {
        const value = safeNumber(item?.co2e_kg);
        const scope = String(item?.scope || "").trim();
        if (scope === "Scope 1") acc.scope_1_kgco2e += value;
        else if (scope === "Scope 2") acc.scope_2_kgco2e += value;
        else if (scope === "Scope 3") acc.scope_3_kgco2e += value;
        acc.total_kgco2e += value;
        return acc;
      },
      { scope_1_kgco2e: 0, scope_2_kgco2e: 0, scope_3_kgco2e: 0, total_kgco2e: 0 }
    );

    const itemsHaveScopeData =
      itemSummary.scope_1_kgco2e > 0 ||
      itemSummary.scope_2_kgco2e > 0 ||
      itemSummary.scope_3_kgco2e > 0;

    if (itemsHaveScopeData || itemSummary.total_kgco2e > 0) {
      return itemSummary;
    }

    const historySummary = (rawHistory || []).reduce(
      (acc, entry) => {
        acc.scope_1_kgco2e += safeNumber(entry?.scope_1_kgco2e);
        acc.scope_2_kgco2e += safeNumber(entry?.scope_2_kgco2e);
        acc.scope_3_kgco2e += safeNumber(entry?.scope_3_kgco2e);
        acc.total_kgco2e += safeNumber(entry?.total_kgco2e);
        return acc;
      },
      { scope_1_kgco2e: 0, scope_2_kgco2e: 0, scope_3_kgco2e: 0, total_kgco2e: 0 }
    );

    return historySummary;
  }, [rawData, items, rawHistory]);

  // Filtered items for table
  const filteredItems = useMemo(() => {
    if (scopeFilter === "all") return items;
    return items.filter((i) => i?.scope === scopeFilter);
  }, [items, scopeFilter]);

  // Time-filtered history (used for time-series)
  const history = useMemo(() => {
    const enriched = (rawHistory || []).map((h) => ({
      ...h,
      chart_date: h.activity_date || h.uploaded_at,
    }));

    return filterByTimeframe(enriched, timeframe, "chart_date");
  }, [rawHistory, timeframe]);

  // Timeframe total from history
  const timeframeTotal = useMemo(() => {
    return (history || []).reduce((acc, h) => acc + safeNumber(h.total_kgco2e), 0);
  }, [history]);

  // Timeframe-adjusted summary
  const summary = useMemo(() => {
    if (timeframe === "all") return baseSummary;

    const baseTotal = safeNumber(baseSummary.total_kgco2e);
    if (baseTotal <= 0 || timeframeTotal <= 0) {
      return { scope_1_kgco2e: 0, scope_2_kgco2e: 0, scope_3_kgco2e: 0, total_kgco2e: 0 };
    }

    const p1 = safeNumber(baseSummary.scope_1_kgco2e) / baseTotal;
    const p2 = safeNumber(baseSummary.scope_2_kgco2e) / baseTotal;
    const p3 = safeNumber(baseSummary.scope_3_kgco2e) / baseTotal;

    return {
      scope_1_kgco2e: timeframeTotal * p1,
      scope_2_kgco2e: timeframeTotal * p2,
      scope_3_kgco2e: timeframeTotal * p3,
      total_kgco2e: timeframeTotal,
    };
  }, [timeframe, baseSummary, timeframeTotal]);

  const hasData = useMemo(() => {
    return items.length > 0 || (rawHistory && rawHistory.length > 0);
  }, [items, rawHistory]);

  const performance = useMemo(() => {
    const total = safeNumber(summary?.total_kgco2e);
    const baseline = safeNumber(sustainabilitySettings?.baseline_emissions_kgco2e);
    const targetReduction = safeNumber(sustainabilitySettings?.target_reduction_percent);

    const baselineChangePct = baseline > 0 ? ((total - baseline) / baseline) * 100 : null;

    let targetProgressPct = null;
    if (baseline > 0 && targetReduction > 0 && targetReduction <= 100) {
      const achievedReduction = ((baseline - total) / baseline) * 100;
      const rawProgress = (achievedReduction / targetReduction) * 100;
      targetProgressPct = Math.max(0, Math.min(100, rawProgress));
    }

    return { baselineChangePct, targetProgressPct };
  }, [summary, sustainabilitySettings]);

  const scopeCounts = useMemo(() => {
    return {
      s1: items.filter((i) => i.scope === "Scope 1").length,
      s2: items.filter((i) => i.scope === "Scope 2").length,
      s3: items.filter((i) => i.scope === "Scope 3").length,
    };
  }, [items]);

  const scopeBreakdown = useMemo(() => {
    const total = safeNumber(summary.total_kgco2e);
    return [
      { label: "Scope 1", value: safeNumber(summary.scope_1_kgco2e), tone: "scope-one" },
      { label: "Scope 2", value: safeNumber(summary.scope_2_kgco2e), tone: "scope-two" },
      { label: "Scope 3", value: safeNumber(summary.scope_3_kgco2e), tone: "scope-three" },
    ].map((scope) => ({
      ...scope,
      percent: total > 0 ? (scope.value / total) * 100 : 0,
    }));
  }, [summary]);

  const nextStep = useMemo(() => {
    const baseline = safeNumber(sustainabilitySettings?.baseline_emissions_kgco2e);
    const target = safeNumber(sustainabilitySettings?.target_reduction_percent);
    if (baseline <= 0) {
      return {
        title: "Set your reporting baseline",
        description: "A baseline lets you compare progress and measure meaningful emissions reductions.",
        label: "Set baseline",
        path: "/dashboard/sustainability-settings",
      };
    }
    if (target <= 0) {
      return {
        title: "Set a reduction target",
        description: "Turn your emissions baseline into a measurable reduction plan for the organisation.",
        label: "Set target",
        path: "/dashboard/sustainability-settings",
      };
    }
    return {
      title: "Keep your reporting current",
      description: "Upload the next bill, invoice or activity record to maintain an accurate emissions picture.",
      label: "Upload data",
      path: "/dashboard/upload",
    };
  }, [sustainabilitySettings]);
  // YoY (frontend-only)
  const yoy = useMemo(() => {
    const now = new Date();
    const thisYear = now.getFullYear();
    const lastYear = thisYear - 1;

    const all = Array.isArray(rawHistory) ? rawHistory : [];

    const yearTotal = (y) =>
      all.reduce((acc, h) => {
        const ts = h?.uploaded_at ? new Date(h.uploaded_at) : null;
        if (!ts || Number.isNaN(ts.getTime())) return acc;
        if (ts.getFullYear() !== y) return acc;
        return acc + safeNumber(h.total_kgco2e);
      }, 0);

    const thisYearTotal = yearTotal(thisYear);
    const lastYearTotal = yearTotal(lastYear);

    const pct = lastYearTotal > 0 ? ((thisYearTotal - lastYearTotal) / lastYearTotal) * 100 : null;

    const direction = pct == null ? "none" : pct > 0 ? "up" : pct < 0 ? "down" : "flat";

    const tone =
      direction === "none"
        ? "muted"
        : direction === "down"
        ? "good"
        : direction === "up"
        ? "bad"
        : "muted";

    return { thisYear, lastYear, thisYearTotal, lastYearTotal, pct, direction, tone };
  }, [rawHistory]);

  const handleDownloadPdf = async () => {
    try {
      setDownloading(true);
      toast.loading("Preparing your PDF report...", { id: "pdf" });

      // Send timeframe to pdfReport.js
      await downloadPdfReport({ timeframe });

      toast.success("PDF report downloaded", { id: "pdf" });
    } catch (err) {
      toast.error(err?.message || "Failed to download PDF", { id: "pdf" });
    } finally {
      setDownloading(false);
    }
  };

  // Delete handler wired to backend
  const handleRemoveRow = async (item) => {
    const entryId = item?.id;

    if (!entryId) {
      toast.error("This entry has no ID yet. Ensure your backend assigns IDs to parsed_items.");
      return;
    }

    const ok = window.confirm("Remove this entry? This cannot be undone.");
    if (!ok) return;

    try {
      setDeletingId(entryId);
      toast.loading("Removing entry...", { id: "rm" });
      await deleteEntryApi(entryId);
      toast.success("Entry removed", { id: "rm" });
      await reloadDashboard();
    } catch (err) {
      toast.error(err?.message || "Failed to remove entry", { id: "rm" });
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="summary-row">
          <Skeleton height={160} />
          <Skeleton height={160} />
          <Skeleton height={160} />
        </div>
        <div className="charts-row">
          <Skeleton height={300} />
          <Skeleton height={300} />
        </div>
        <div className="charts-row">
          <Skeleton height={320} />
        </div>
      </div>
    );
  }

  if (!hasData) {
    return (
      <div className="dashboard-page overview-dashboard empty-dashboard">
        <div className="empty-card">
          <h1>Welcome to your carbon workspace</h1>
          <p>Your dashboard will populate once you upload a file or add your first manual entry.</p>

          <div className="empty-steps">
            <div className="empty-step">
              <span>1</span> Upload invoices, bills or reports
            </div>
            <div className="empty-step">
              <span>2</span> We classify and calculate emissions
            </div>
            <div className="empty-step">
              <span>3</span> Review trends, targets and reports
            </div>
          </div>

          <div className="empty-actions">
            <button className="btn-primary" onClick={() => navigate("/dashboard/upload")}>
              Upload your first file
            </button>
            <button className="btn-secondary" onClick={() => navigate("/dashboard/sustainability-settings")}>
              Set baseline and target
            </button>
          </div>
        </div>
      </div>
    );
  }

  const yoyStyles = {
    row: { display: "flex", alignItems: "center", gap: 8, marginTop: 10 },
    badge: (tone) => ({
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      border: "1px solid var(--border)",
      background:
        tone === "good"
          ? "rgba(16, 185, 129, 0.12)"
          : tone === "bad"
          ? "rgba(249, 115, 22, 0.12)"
          : "rgba(100, 116, 139, 0.10)",
      color: tone === "good" ? "rgb(16, 185, 129)" : tone === "bad" ? "rgb(249, 115, 22)" : "var(--muted)",
    }),
    arrow: { fontSize: 12, lineHeight: "12px", marginTop: -1 },
    label: { fontSize: 12, color: "var(--muted)", fontWeight: 600 },
  };

  const arrowSymbol = yoy.direction === "up" ? "+" : yoy.direction === "down" ? "-" : "o";

  return (
    <div className="dashboard-page overview-dashboard">
      {/* HEADER */}
      <div className="dashboard-header overview-hero">
        <div className="overview-hero-copy">
          <p className="overview-eyebrow">Sustainability intelligence</p>
          <h1>Carbon overview</h1>
          <p className="subtitle">Understand your current emissions picture, focus on what needs attention, and keep reporting on track.</p>
          {client ? (
            <p className="overview-client-label">
              Reporting workspace: <b>{client}</b>
            </p>
          ) : null}
        </div>

        <div className="dashboard-header-actions overview-hero-actions">
          <TimeframeSelector value={timeframe} onChange={setTimeframe} />

          <button className="btn-secondary" onClick={handleDownloadPdf} disabled={downloading}>
            {downloading ? "Preparing PDF..." : "Download PDF report"}
          </button>

          <button className="btn-primary" onClick={() => navigate("/dashboard/upload")}>
            Upload data
          </button>
        </div>
      </div>

      <section className="overview-snapshot">
        <div className="overview-section-heading">
          <div><p className="overview-eyebrow">Reporting snapshot</p><h2>Emissions at a glance</h2></div>
          <span className="overview-period-label">{timeframe === "all" ? "All available data" : "Selected period"}</span>
        </div>

        <div className="overview-primary-grid">
          <article className="card kpi-card overview-total-card">
            <p className="kpi-label">Total emissions</p>
            <h2>{Math.round(summary.total_kgco2e).toLocaleString()} kg CO2e</h2>
            {timeframe === "all" ? (
              <div style={yoyStyles.row}>
                <span style={yoyStyles.badge(yoy.tone)}>
                  <span style={yoyStyles.arrow}>{arrowSymbol}</span>
                  {formatPct(yoy.pct)}
                </span>
                <span style={yoyStyles.label}>vs last year</span>
              </div>
            ) : (
              <span className="kpi-sub">Selected timeframe</span>
            )}
            {timeframe === "all" ? <span className="kpi-sub">All time</span> : null}
          </article>

          <article className="card overview-scope-pulse">
            <div className="overview-panel-heading">
              <div><p className="overview-eyebrow">Scope pulse</p><h2>Where emissions sit</h2></div>
              <span>{Math.round(summary.total_kgco2e).toLocaleString()} kg CO2e</span>
            </div>
            <div className="scope-pulse-list">
              {scopeBreakdown.map((scope) => (
                <div className="scope-pulse-row" key={scope.label}>
                  <div className="scope-pulse-label"><span className={`scope-dot ${scope.tone}`} /><strong>{scope.label}</strong><em>{scope.percent.toFixed(1)}%</em></div>
                  <div className="scope-pulse-track"><span className={scope.tone} style={{ width: `${scope.percent}%` }} /></div>
                </div>
              ))}
            </div>
          </article>
        </div>

        <div className="perf-row">
          <div className="card perf-card">
            <p className="kpi-label">Baseline comparison</p>
            <h2>
              {performance.baselineChangePct == null
                ? "-"
                : `${performance.baselineChangePct > 0 ? "+" : ""}${performance.baselineChangePct.toFixed(1)}%`}
            </h2>
            <span className="kpi-sub">Current emissions vs baseline emissions</span>
          </div>

          <div className="card perf-card">
            <p className="kpi-label">Target reduction progress</p>
            <h2>
              {performance.targetProgressPct == null
                ? "-"
                : `${Math.round(performance.targetProgressPct)}%`}
            </h2>
            <div className="progress-track" aria-label="Target progress bar">
              <div className="progress-fill" style={{ width: `${performance.targetProgressPct || 0}%` }} />
            </div>
            <span className="kpi-sub">
              {performance.targetProgressPct === null
                ? "Set baseline and target values to track progress"
                : safeNumber(performance.baselineChangePct) > 0
                ? "Progress begins once emissions fall below baseline"
                : "Progress towards target reduction"}
            </span>
          </div>
        </div>

        <div className="overview-action-grid">
          <article className="card overview-next-step">
            <p className="overview-eyebrow">Recommended next step</p>
            <h2>{nextStep.title}</h2>
            <p>{nextStep.description}</p>
            <button className="btn-primary" type="button" onClick={() => navigate(nextStep.path)}>{nextStep.label}</button>
          </article>
          <div className="overview-stat-grid">
            <article className="card kpi-card overview-stat-card">
              <p className="kpi-label">Entries processed</p>
              <h2>{items.length}</h2>
              <span className="kpi-sub">From parsed documents and manual entries</span>
            </article>
            <article className="card kpi-card overview-stat-card">
              <p className="kpi-label">Uploads</p>
              <h2>{history.length}</h2>
              <span className="kpi-sub">In selected timeframe</span>
            </article>
          </div>
        </div>
      </section>
      {/* Hidden print chart nodes used by html-to-image in pdfReport.js */}
      <div style={{ position: "absolute", left: "-100000px", top: 0, width: 900, height: 0, overflow: "hidden" }}>
        <div id="pdf-chart-trend">
          <OverallEmissionsLineChart history={history} mode="cumulative" />
        </div>
        <div id="pdf-chart-pie">
          <PieChartScopes summary={summary} />
        </div>
        <div id="pdf-chart-bar">
          <BarChartScopes summary={summary} />
        </div>
      </div>

      <section className="overview-reporting-section">
        <div className="overview-section-heading">
          <div><p className="overview-eyebrow">Carbon reporting</p><h2>Trends and composition</h2></div>
          <p>Use these views to understand how total emissions are moving and which scopes contribute most.</p>
        </div>
      {/* ROW 1 */}
      <div className="charts-row">
        <div className="card chart-card overview-trend-card">
          <div className="card-header">
            <h3>Overall emissions trend</h3>
            <span className="pill pill-soft">{timeframe === "all" ? "All time" : "Timeframe"}</span>
          </div>
          <OverallEmissionsLineChart history={history} mode="cumulative" />
        </div>

        <div className="card chart-card overview-breakdown-card">
          <div className="card-header">
            <h3>Emissions breakdown (by scope)</h3>
          </div>
          <PieChartScopes summary={summary} />
        </div>
      </div>

      {/* ROW 2 */}
      <div className="charts-row">
        <div className="card chart-card wide">
          <div className="card-header" style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
            <div>
              <h3>Per-scope trends</h3>
              <p className="subtitle" style={{ marginTop: 6, opacity: 0.8 }}>
                Scope 1 / 2 / 3 trend estimated from current scope proportions (until per-scope history is stored).
              </p>
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                className={`btn-secondary ${scopeTrendMode === "cumulative" ? "active" : ""}`}
                type="button"
                onClick={() => setScopeTrendMode("cumulative")}
                title="Running total over time"
              >
                Cumulative
              </button>
              <button
                className={`btn-secondary ${scopeTrendMode === "daily" ? "active" : ""}`}
                type="button"
                onClick={() => setScopeTrendMode("daily")}
                title="Per-day totals"
              >
                Daily
              </button>
            </div>
          </div>

          <ScopeTrendsLineChart history={history} baseSummary={baseSummary} mode={scopeTrendMode} />
        </div>
      </div>

      {/* ROW 3 */}
      <div className="charts-row">
        <div className="card chart-card wide">
          <div className="card-header">
            <h3>Scope comparison</h3>
          </div>
          <BarChartScopes summary={summary} />
        </div>
      </div>

      </section>

      {/* TABLE */}
      <section className="table-section overview-data-section">
        <div className="table-header">
          <div><p className="overview-eyebrow">Data register</p><h2>Parsed entries</h2></div>
          <span className="pill pill-soft">
            Scope 1: {scopeCounts.s1} | Scope 2: {scopeCounts.s2} | Scope 3: {scopeCounts.s3}
          </span>
        </div>

        {/* Scope filter buttons */}
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          <button
            className={`btn-secondary ${scopeFilter === "all" ? "active" : ""}`}
            type="button"
            onClick={() => setScopeFilter("all")}
          >
            All
          </button>
          <button
            className={`btn-secondary ${scopeFilter === "Scope 1" ? "active" : ""}`}
            type="button"
            onClick={() => setScopeFilter("Scope 1")}
          >
            Scope 1
          </button>
          <button
            className={`btn-secondary ${scopeFilter === "Scope 2" ? "active" : ""}`}
            type="button"
            onClick={() => setScopeFilter("Scope 2")}
          >
            Scope 2
          </button>
          <button
            className={`btn-secondary ${scopeFilter === "Scope 3" ? "active" : ""}`}
            type="button"
            onClick={() => setScopeFilter("Scope 3")}
          >
            Scope 3
          </button>

          <span style={{ marginLeft: "auto", fontSize: 12, opacity: 0.75, alignSelf: "center" }}>
            Showing {scopeFilter === "all" ? items.length : filteredItems.length} entries
          </span>
        </div>

        <div className="table-card">
          {/* Scroll wrapper: ~5 rows then scroll */}
          <div style={{ maxHeight: 260, overflowY: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Value</th>
                  <th>Unit</th>
                  <th>Scope</th>
                  <th>CO2e (kg)</th>
                  <th style={{ width: 140 }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item, idx) => {
                  const rowKey = item?.id || `${item?.uploaded_at || "na"}-${idx}`;
                  const isDeleting = deletingId && item?.id === deletingId;

                  return (
                    <tr key={rowKey}>
                      <td>{item.value}</td>
                      <td>{item.unit}</td>
                      <td>{item.scope}</td>
                      <td>{item.co2e_kg}</td>
                      <td style={{ textAlign: "right" }}>
                        <button
                          className="btn-secondary"
                          type="button"
                          onClick={() => handleRemoveRow(item)}
                          disabled={!item?.id || isDeleting}
                          title={!item?.id ? "Missing entry id (backend must assign IDs)" : "Remove this entry"}
                        >
                          {isDeleting ? "Removing..." : "Remove"}
                        </button>
                      </td>
                    </tr>
                  );
                })}

                {filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ padding: 14, opacity: 0.8 }}>
                      No parsed entries match this filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}


